import { useEffect, useState } from 'react';
import { Stack, Typography, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';

import { useAlert } from '@/contexts/Alert/AlertContext';
import { useLoading } from '@/shared/context/UIContextProvider';
import { getProjectById, saveProject, saveUnit } from '@/data';
import { Project } from '@urbanmix-tech/shared-js';

import { ConfirmButtons } from '../shared/ConfirmButtons/ConfirmButtons';
import { Input } from '../shared/Inputs/Input';
import { QuickCreateForm } from '../BuildingCreation/QuickCreateForm';
import { convertToUnit } from '@/services/buildingGenerator';

export type NewProjectModalContentProps = {
  projectId?: string;
  onClose(): void;
};

export function NewProjectModalContent({
  projectId,
  onClose,
}: NewProjectModalContentProps) {
  const { showAlert } = useAlert();
  const { createLoading } = useLoading();
  const navigate = useNavigate();

  const [oldProject, setOldProject] = useState<Project | null>(null);
  const [createMode, setCreateMode] = useState<'manual' | 'quick'>('manual');

  // Generate projectId once when component mounts for quick create mode
  const [quickCreateProjectId] = useState(() => uuidv4());

  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [address, setAddress] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [buildingType, setBuildingType] = useState('');
  const [yearBuilt, setYearBuilt] = useState(new Date().getFullYear());

  useEffect(() => {
    if (projectId) {
      getProjectById(projectId).then((value) => {
        if (value && value.buildings.length > 0) {
          setOldProject(value);

          setProjectName(value.name);
          setClientName(value.clientName);
          setAddress(value.address);
          setBuildingName(value.buildings[0].name);
          setBuildingType(value.buildings[0].type);
          setYearBuilt(value.buildings[0].yearBuilt);
        } else {
          showAlert('Building not exists!', 'error');
        }
      });
    }
  }, [projectId, showAlert]);

  const createNewProject = async () => {
    const newProjectId = uuidv4();

    const loader = createLoading();

    loader.startLoading({
      title: 'Saving new building...',
    });

    const newProject: Project = {
      id: newProjectId,
      clientName,
      buildings: [
        {
          id: 1,
          floors: 0,
          yearBuilt,
          type: buildingType as 'Residential' | 'Commercial' | 'Industrial',
          '3dData': {
            cameraConf: {
              polarAngle: 1,
              azimuthAngle: 1,
              distance: 49,
              target: [-0.8, 15.5, 2.7],
            },
          },
          coverImagePath: null,
          unitSummary: {
            affordableRent: {
              sumOfAffordableRent: 0,
              units: 0,
              sumOfSQFT: 0,
            },
            summariesByType: {},
            marketRent: {
              sumOfSQFT: 0,
              sumOfMarketRent: 0,
              units: 0,
            },
            unitCnt: {
              TURNOVER: {},
              INSPECT: {},
              LEASE: {},
              DONE: {},
              NOTICE: {},
            },
          },
          modelPath: null,
          name: buildingName,
        },
      ],
      name: projectName,
      address,
      coverImagePath: null,
    };

    await saveProject(newProject);

    loader.stopLoading();
  };

  const handleSaveProject = async () => {
    if (projectId && oldProject) {
      const newProject: Project = {
        ...oldProject,
        clientName,
        name: projectName,
        address,
        buildings: [
          {
            ...oldProject.buildings[0],
            type: buildingType as 'Residential' | 'Commercial' | 'Industrial',
            yearBuilt,
            name: buildingName,
          },
        ],
      };

      await saveProject(newProject);
    } else {
      await createNewProject();
    }

    onClose();
    window.location.reload();
  };

  const handleQuickCreateComplete = async (result: {
    modelUrl: string;
    buildingData: any;
    units: any[];
  }) => {
    // Use the pre-generated projectId instead of generating a new one
    const newProjectId = quickCreateProjectId;
    const loader = createLoading();

    loader.startLoading({
      title: 'Saving building and units...',
    });

    try {
      // Create project with generated model
      const newProject: Project = {
        id: newProjectId,
        clientName: clientName || 'Quick Create',
        buildings: [
          {
            id: 1,
            floors: result.buildingData.floors || 0,
            yearBuilt,
            type: 'Residential',
            '3dData': {
              cameraConf: {
                polarAngle: 1,
                azimuthAngle: 1,
                distance: 49,
                target: [-0.8, 15.5, 2.7],
              },
            },
            coverImagePath: null,
            unitSummary: {
              affordableRent: {
                sumOfAffordableRent: 0,
                units: 0,
                sumOfSQFT: 0,
              },
              summariesByType: {},
              marketRent: {
                sumOfSQFT: 0,
                sumOfMarketRent: 0,
                units: 0,
              },
              unitCnt: {
                TURNOVER: {},
                INSPECT: {},
                LEASE: {},
                DONE: {},
                NOTICE: {},
              },
            },
            modelPath: result.modelUrl,
            name: result.buildingData.name,
          },
        ],
        name: result.buildingData.name,
        address: result.buildingData.address,
        coverImagePath: null,
      };

      await saveProject(newProject);

      // Save all units
      for (const generatedUnit of result.units) {
        const unit = convertToUnit(generatedUnit, newProjectId, 1);
        await saveUnit(newProjectId, unit as any);
      }

      showAlert('Building created successfully! Redirecting to rent roll...', 'success');
      loader.stopLoading();
      onClose();

      // Navigate to rent roll page for the newly created project
      navigate(`/m/projects/${newProjectId}/1/rentroll`);
    } catch (error) {
      console.error('Error saving quick create project:', error);
      showAlert('Failed to save project', 'error');
      loader.stopLoading();
    }
  };

  const anyEmpty =
    [projectName, clientName, address, buildingName, buildingType].findIndex(
      (item) => item.trim() === ''
    ) !== -1;

  // If editing existing project, don't show quick create
  if (projectId) {
    return (
      <Stack
        gap="24px"
        className="relative w-[500px] rounded-[10px] bg-neutral-950 p-24"
      >
        <Typography variant="h4" className="!text-white">
          Edit Building
        </Typography>
        <Input
          onChange={(e) => setProjectName(e.currentTarget.value)}
          value={projectName}
          type="text"
          fullWidth
          placeholder="Project Name*"
          className="border border-primary text-stone-200"
          required
        />
        <Input
          onChange={(e) => setClientName(e.currentTarget.value)}
          value={clientName}
          type="text"
          fullWidth
          placeholder="Project Client Name*"
          className="border border-primary text-stone-200"
          required
        />
        <Input
          onChange={(e) => setAddress(e.currentTarget.value)}
          value={address}
          type="text"
          fullWidth
          placeholder="Project Address*"
          className="border border-primary text-stone-200"
          required
        />
        <Input
          onChange={(e) => setBuildingName(e.currentTarget.value)}
          value={buildingName}
          type="text"
          fullWidth
          placeholder="Building Name*"
          className="border border-primary text-stone-200"
          required
        />
        <Input
          onChange={(e) => setBuildingType(e.currentTarget.value)}
          value={buildingType}
          type="text"
          fullWidth
          placeholder="Building Type*"
          className="border border-primary text-stone-200"
          required
        />
        <Input
          onChange={(e) => setYearBuilt(+e.currentTarget.value)}
          value={yearBuilt}
          type="number"
          inputProps={{
            min: '1500',
            max: `${new Date().getFullYear()}`,
          }}
          fullWidth
          placeholder="Year Built*"
          className="border border-primary text-stone-200"
          required
        />
        <ConfirmButtons
          confirm={{
            label: 'Edit',
            disabled: anyEmpty,
            onClick: handleSaveProject,
          }}
          cancel={{
            label: 'Cancel',
            onClick: onClose,
          }}
        />
      </Stack>
    );
  }

  return (
    <Box className="relative rounded-[10px] bg-neutral-950 p-24">
      <Stack gap="24px">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" className="!text-white">
            Add New Building
          </Typography>
          <ToggleButtonGroup
            value={createMode}
            exclusive
            onChange={(_, value) => value && setCreateMode(value)}
            size="small"
          >
            <ToggleButton value="manual">Manual</ToggleButton>
            <ToggleButton value="quick">Quick Create</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {createMode === 'quick' ? (
          <QuickCreateForm
            projectId={quickCreateProjectId}
            onComplete={handleQuickCreateComplete}
            onCancel={onClose}
          />
        ) : (
          <Stack gap="24px">
            <Input
              onChange={(e) => setProjectName(e.currentTarget.value)}
              value={projectName}
              type="text"
              fullWidth
              placeholder="Project Name*"
              className="border border-primary text-stone-200"
              required
            />
            <Input
              onChange={(e) => setClientName(e.currentTarget.value)}
              value={clientName}
              type="text"
              fullWidth
              placeholder="Project Client Name*"
              className="border border-primary text-stone-200"
              required
            />
            <Input
              onChange={(e) => setAddress(e.currentTarget.value)}
              value={address}
              type="text"
              fullWidth
              placeholder="Project Address*"
              className="border border-primary text-stone-200"
              required
            />
            <Input
              onChange={(e) => setBuildingName(e.currentTarget.value)}
              value={buildingName}
              type="text"
              fullWidth
              placeholder="Building Name*"
              className="border border-primary text-stone-200"
              required
            />
            <Input
              onChange={(e) => setBuildingType(e.currentTarget.value)}
              value={buildingType}
              type="text"
              fullWidth
              placeholder="Building Type*"
              className="border border-primary text-stone-200"
              required
            />
            <Input
              onChange={(e) => setYearBuilt(+e.currentTarget.value)}
              value={yearBuilt}
              type="number"
              inputProps={{
                min: '1500',
                max: `${new Date().getFullYear()}`,
              }}
              fullWidth
              placeholder="Year Built*"
              className="border border-primary text-stone-200"
              required
            />
            <ConfirmButtons
              confirm={{
                label: 'Create',
                disabled: anyEmpty,
                onClick: handleSaveProject,
              }}
              cancel={{
                label: 'Cancel',
                onClick: onClose,
              }}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
